import { KeyboardEventHandler, useCallback } from 'react';

/**
 * 操作に対応するキーのリスト
 *
 * 追加する際は下記を参照
 * cf: https://developer.mozilla.org/ja/docs/Web/API/UI_Events/Keyboard_event_key_values
 */
export const keyList = {
  UP: 'ArrowUp',
  DOWN: 'ArrowDown',
  ENTER: 'Enter',
} as const;
type Key = (typeof keyList)[keyof typeof keyList];

/**
 * (IME操作以外の)キーボードの特定のキー操作に対応するイベントハンドラを作成する。
 *
 * @param key
 * @param callback
 */
export const useKeyAction = <T extends Element = HTMLElement>(
  key: Key,
  callback: KeyboardEventHandler<T>
): KeyboardEventHandler<T> => {
  return useCallback(
    (e) => {
      // FireFox における IME 操作中に keydown イベントがトリガされる仕様の回避
      // 例外的に keyCode を参照する。
      // cf: https://developer.mozilla.org/ja/docs/Web/API/Element/keydown_event
      if (e.nativeEvent.isComposing || e.keyCode === 229) {
        return;
      }

      if (e.key === key) {
        callback(e);
      }
    },
    [key, callback]
  );
};

/**
 * 複数の keydown イベントハンドラをまとめた keydown イベントハンドラを返却する
 *
 * @param handlers
 */
export const composeKeyAction = <T extends Element = HTMLElement>(
  ...handlers: KeyboardEventHandler<T>[]
): KeyboardEventHandler<T> => {
  return (e) => {
    handlers.forEach((handler) => handler(e));
  };
};
